/**
 * Spaced Repetition nach SM-2.
 *
 * Der Algorithmus ist von 1987 und hat sich gehalten, weil er mit drei Zahlen je Karte
 * auskommt: Wiederholungen, Leichtigkeit, Abstand. Alles hier sind reine Funktionen,
 * damit der Lernstand prüfbar bleibt und nicht am Speicher hängt.
 */

export interface Card {
  /** Was gelernt wird: "state:r23-power-ge50" oder "buoy:cardinal-south". */
  id: string
  /** Zahl der aufeinanderfolgenden richtigen Antworten. */
  repetitions: number
  /** Leichtigkeit, startet bei 2,5 und fällt nie unter 1,3. */
  ease: number
  /** Abstand bis zur nächsten Wiederholung in Tagen. */
  intervalDays: number
  /** Tag der nächsten Wiederholung, als Zahl der Tage seit 1970. */
  dueDay: number
  /** Zahl der Versuche und der richtigen Antworten, für den Fortschritt. */
  attempts: number
  correct: number
}

/** Note nach SM-2: 0 bis 5. */
export type Grade = 0 | 1 | 2 | 3 | 4 | 5

export const MIN_EASE = 1.3
export const START_EASE = 2.5

export function newCard(id: string, today = dayNumber()): Card {
  return { id, repetitions: 0, ease: START_EASE, intervalDays: 0, dueDay: today, attempts: 0, correct: 0 }
}

export function dayNumber(date = new Date()): number {
  return Math.floor(date.getTime() / 86_400_000)
}

/**
 * Note aus einer Quizantwort. Richtig und schnell ist eine 5, richtig mit Zögern eine 4,
 * falsch eine 2: das ist die übliche Zuordnung, wenn man keine Selbsteinschätzung hat.
 */
export function gradeFor(correct: boolean, seconds: number): Grade {
  if (!correct) return 2
  return seconds <= 6 ? 5 : 4
}

/**
 * Ein Durchgang. Gibt die neue Karte zurück, die alte bleibt unverändert.
 *
 * Reihenfolge wie bei Wozniak: **erst das Intervall mit der bisherigen Leichtigkeit
 * rechnen, dann die Leichtigkeit anpassen.** Wer die neue Leichtigkeit schon im Intervall
 * verwendet, bekommt bei jeder Notenänderung ab der dritten Wiederholung andere Abstände.
 */
export function review(card: Card, grade: Grade, today = dayNumber()): Card {
  const passed = grade >= 3
  const repetitions = passed ? card.repetitions + 1 : 0
  const intervalDays = !passed
    ? 1
    : repetitions === 1
      ? 1
      : repetitions === 2
        ? 6
        : Math.round(card.intervalDays * card.ease)
  const ease = Math.max(MIN_EASE, card.ease + (0.1 - (5 - grade) * (0.08 + (5 - grade) * 0.02)))
  return {
    ...card,
    ease,
    repetitions,
    intervalDays,
    dueDay: today + intervalDays,
    attempts: card.attempts + 1,
    correct: card.correct + (passed ? 1 : 0),
  }
}

/** Fällige Karten zuerst, danach die schwächsten: Schwachstellen steuern die Wiederholung. */
export function dueCards(cards: Card[], today = dayNumber()): Card[] {
  return cards
    .filter((c) => c.dueDay <= today)
    .sort((a, b) => a.dueDay - b.dueDay || successRate(a) - successRate(b))
}

export function successRate(card: Card): number {
  return card.attempts === 0 ? 0 : card.correct / card.attempts
}
